import { IS_NATERA } from '@constants/constants';
import React, { useMemo, useState } from 'react';

import { Container, Stack } from 'react-bootstrap';

import { ReactComponent as NateraLogoSvg } from '@assets/images/natera/natera-web-logo.svg';
// Import Natera social media SVG icons
import { ReactComponent as FacebookIcon } from '@assets/images/natera/socials/facebook.svg';
import { ReactComponent as FacebookHoverIcon } from '@assets/images/natera/socials/facebook-hover.svg';
import { ReactComponent as LinkedinIcon } from '@assets/images/natera/socials/linkedin.svg';
import { ReactComponent as LinkedinHoverIcon } from '@assets/images/natera/socials/linkedin-hover.svg';
import { ReactComponent as XIcon } from '@assets/images/natera/socials/x.svg';
import { ReactComponent as XHoverIcon } from '@assets/images/natera/socials/x-hover.svg';
import { ReactComponent as InstagramIcon } from '@assets/images/natera/socials/Instagram.svg';
import { ReactComponent as InstagramHoverIcon } from '@assets/images/natera/socials/Instagram-hover.svg';

import './Footer.scss';

const links: Array<{ text: string; url: string }> = [
  {
    text: 'HIPAA Notice of Privacy Practices',
    url: 'https://myome.com/legal/hipaa-notice-of-privacy-practices',
  },
  {
    text: 'Terms of Service',
    url: 'https://myome.com/legal/terms-of-service',
  },
  {
    text: 'Privacy Notice',
    url: 'https://myome.com/legal/privacy-notice',
  },
];

const nateraLinks: Array<{ text: string; url: string }> = [
  {
    text: 'Natera.com',
    url: 'https://www.natera.com/',
  },
  {
    text: 'Privacy Policy',
    url: 'https://www.natera.com/privacy/',
  },
  {
    text: 'Terms of Use',
    url: 'https://www.natera.com/terms/',
  },
  {
    text: 'Notice of Privacy Practices',
    url: 'https://www.natera.com/notice-of-privacy-practices/',
  },
  {
    text: 'Natera Licenses',
    url: 'https://www.natera.com/natera-licenses/',
  },
  {
    text: 'MyOme Licenses',
    url: 'https://myome.com/about-us/licenses-and-certifications',
  },
];

const nateraSocialLinks: Array<{ icon: string; url: string; label: string }> = [
  {
    icon: 'facebook',
    url: 'https://www.facebook.com/NateraGenetics',
    label: 'Facebook',
  },
  {
    icon: 'linkedin',
    url: 'https://www.linkedin.com/company/natera/',
    label: 'LinkedIn',
  },
  {
    icon: 'x',
    url: 'https://x.com/nateragenetics',
    label: 'X (Twitter)',
  },
  {
    icon: 'instagram',
    url: 'https://www.instagram.com/nateragenetics/',
    label: 'Instagram',
  },
];

// Icon mapping for social media icons
const socialIconMap = {
  facebook: { default: FacebookIcon, hover: FacebookHoverIcon },
  linkedin: { default: LinkedinIcon, hover: LinkedinHoverIcon },
  x: { default: XIcon, hover: XHoverIcon },
  instagram: { default: InstagramIcon, hover: InstagramHoverIcon },
};

// Social icon component with hover effects
const SocialIcon = ({ iconKey, url, label }: { iconKey: string; url: string; label: string }) => {
  const [isHovered, setIsHovered] = useState(false);
  const icons = socialIconMap[iconKey as keyof typeof socialIconMap];

  if (!icons) return null;

  const IconComponent = isHovered ? icons.hover : icons.default;

  return (
    <a
      href={url}
      target='_blank'
      rel='noreferrer'
      className='d-flex align-items-center text-decoration-none social-icon-link'
      title={label}
      aria-label={label}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconComponent className='social-icon' />
    </a>
  );
};

const NateraSocialSection = () => (
  <div className='natera-social-section-wrapper'>
    <Container fluid='xxl'>
      <div className='d-flex flex-column flex-md-row gap-3 mb-3 py-3 natera-social-section'>
        <a href='https://www.natera.com/' target='_blank' rel='noreferrer'>
          <NateraLogoSvg className='natera-logo' />
        </a>
        <ul className='d-flex flex-wrap gap-3 ms-md-auto list-unstyled mb-0'>
          {nateraSocialLinks.map(link => (
            <li key={link.url}>
              <SocialIcon iconKey={link.icon} url={link.url} label={link.label} />
            </li>
          ))}
        </ul>
      </div>
    </Container>
  </div>
);

const Footer = () => {
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <footer className={`bg-white ${IS_NATERA ? '' : 'border-cyan border-top'} pt-3 pb-4`}>
      {IS_NATERA && <NateraSocialSection />}
      <Container fluid='xxl'>
        <Stack gap={3} className='text-secondary'>
          <ul
            className='d-flex flex-wrap gap-2 list-group-numbered ps-0 mb-0'
            role='navigation'
            aria-label='footer nav'
          >
            {(IS_NATERA ? nateraLinks : links).map((link, index, arr) => (
              <React.Fragment key={link.url}>
                <li>
                  <a
                    href={link.url}
                    target='_blank'
                    rel='noreferrer'
                    className={`d-flex fs-sm fw-medium text-decoration-none ${IS_NATERA ? 'natera-footer-link' : ''}`}
                  >
                    {link.text}
                  </a>
                </li>
                {index !== arr.length - 1 && <div className='lh-sm'>|</div>}
              </React.Fragment>
            ))}
          </ul>
          {IS_NATERA ? (
            <>
              <p className='fs-xs mb-0'>© {currentYear} Natera, Inc. All Rights Reserved.</p>
              <p className='fs-xs mb-0'>
                The test described has been developed and its performance characteristics determined by Myome Inc., a
                clinical laboratory certified by the Clinical Laboratory Improvement Amendments of 1988 (CLIA) and
                College of American Pathologists (CAP) accredited to perform high complexity clinical laboratory
                testing. This test has not been cleared or approved by the U.S. Food and Drug Administration (FDA).
                Although FDA does not currently clear or approve laboratory-developed tests in the U.S., certification
                of the laboratory is required under CLIA to ensure the quality and validity of the tests.
              </p>
            </>
          ) : (
            <>
              <div className='d-flex flex-column'>
                <p className='fs-xs mb-0'>© {currentYear} MyOme.com</p>
                <p className='fs-xs mb-0'>
                  MyOme is not responsible for the content or accuracy of third-party websites
                </p>
              </div>
              <p className='mb-0 fs-xs'>
                MyOme&apos;s tests are developed, and the performance characteristics determined, by MyOme, Inc., a
                clinical laboratory certified under the Clinical Laboratory Improvement Amendments of 1988 (CLIA) to
                perform high complexity clinical laboratory testing. MyOme&apos;s tests have not been cleared or
                approved by the U.S. Food and Drug Administration (FDA). MyOme Inc. is College of American Pathologists
                (CAP) accredited and CLIA certified.
              </p>
            </>
          )}
        </Stack>
      </Container>
    </footer>
  );
};

export default Footer;
